import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { aboutData } from '../../data/mock';

gsap.registerPlugin(ScrollTrigger);

const AboutSection = () => {
  const sectionRef = useRef(null);
  const contentRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current,
        { y: 80, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse'
          }
        }
      );

      gsap.fromTo(
        imageRef.current,
        { scale: 1.1, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 1.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse'
          }
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="nosotros"
      ref={sectionRef}
      className="relative py-16 lg:py-28 bg-black overflow-hidden"
      data-testid="about-section"
    >
      {/* Subtle gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Text Content */}
          <div ref={contentRef}>
            <span className="inline-block text-white/50 text-xs font-light tracking-[0.3em] uppercase mb-6">
              Sobre Nosotros
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white leading-tight mb-8">
              {aboutData.title.split(' ').map((word, i) => (
                <span key={i}>
                  {i === 1 ? (
                    <span className="text-white/50 font-medium">{word}</span>
                  ) : (
                    word
                  )}{' '}
                </span>
              ))}
            </h2>

            <div className="space-y-5">
              {aboutData.paragraphs.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-base leading-relaxed font-light text-white/70"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Simple CTA */}
            <div className="mt-8">
              <a
                href="#contacto"
                className="inline-flex items-center gap-3 text-white font-light group hover:text-white/80 transition-colors"
                data-testid="about-cta"
              >
                <span className="border-b border-white/40 group-hover:border-white/60 transition-colors pb-1">
                  Conocé más sobre nosotros
                </span>
                <svg
                  className="w-4 h-4 transform group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>

          {/* Image - Updated with new team photo */}
          <div ref={imageRef} className="relative">
            <div className="relative aspect-[4/5] lg:aspect-[4/4] rounded-2xl overflow-hidden">
              <img
                src={`${process.env.PUBLIC_URL}/images/staff/quienes_somos_nueva.jpg`}
                alt="Gonzalo Sorondo y Sebastián Taborda - Directores FARO Sports"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>

            {/* Decorative line */}
            <div className="absolute -right-4 top-8 bottom-8 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent hidden lg:block" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
